import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

const PAGE_NAME = '202606夏季新款预售活动';
const MARKET_ACTIVITY_NAME = '26年6月夏季新款活动';
const NOTIFYME_SOURCE = 'campaign_page';

export function useFrenchSummerTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  const marketActivityId = md5('FrenchSummer').toString();
  const userStatus = userInfo?.email ? 'logged_in' : 'guest';

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_View
   * @EventDesc 2026夏季新款镜框预售页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: PAGE_NAME,
            page_id: marketActivityId,
            refer_page_name,
            refer_url_path,
            market_activity_id: marketActivityId,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('French Summer page view tracking error:', error);
    }
  }, [trackEvent, getFromRouterInfo, marketActivityId]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_Leave
   * @EventDesc 2026夏季新款镜框预售页面离开
   * @EventParams {
   *   page_name: 页面名称,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   *   view_area: 浏览区域,
   * }
   */
  const trackPageLeave = useCallback(() => {
    // 使用保存的滚动状态
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: PAGE_NAME,
            page_id: marketActivityId,
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: marketActivityId,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('French Summer page leave tracking error:', error);
    }
  }, [trackEvent, marketActivityId]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageNavTab_Click
   * @EventDesc 2026夏季新款镜框预售tab导航点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   tab_name: tab按钮名称,
   * }
   */
  const trackNavTabClick = useCallback(
    (tabName: string, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageNavTab_Click',
            properties: {
              page_name: PAGE_NAME,
              page_id: marketActivityId,
              module_name: '导航位',
              pits_index: index,
              tab_name: tabName,
              market_activity_id: marketActivityId,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer nav tab click tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageViewMoreBtn_Click
   * @EventDesc 2026夏季新款镜框预售view more按钮点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   * }
   */
  const trackViewMoreClick = useCallback(
    (moduleName: string, tabName: string = '', pitsIndex?: number) => {
      try {
        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          tab_name: tabName,
          module_name: moduleName,
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (typeof pitsIndex === 'number') {
          properties.pits_index = pitsIndex;
        }

        trackEvent(
          {
            eventName: 'CampaignPageViewMoreBtn_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer view more click tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageBanner_Click
   * @EventDesc 2026夏季新款镜框预售banner点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   banner_name: banner名称,
   * }
   */
  const trackBannerClick = useCallback(
    (bannerName: string, moduleName = 'Banner图', index = 0) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties: {
              page_name: PAGE_NAME,
              page_id: marketActivityId,
              module_name: moduleName,
              pits_index: index,
              banner_name: bannerName,
              market_activity_id: marketActivityId,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer banner click tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageBanner_Show
   * @EventDesc 2026夏季新款镜框预售banner曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   banner_name: banner名称,
   * }
   */
  const trackBannerShow = useCallback(
    (bannerName: string, moduleName = 'Banner图', index = 0) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties: {
              page_name: PAGE_NAME,
              page_id: marketActivityId,
              module_name: moduleName,
              pits_index: index,
              banner_name: bannerName,
              market_activity_id: marketActivityId,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer banner show tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc 2026夏季新款镜框预售商品点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   *   tab_name: 商品导航tab名称,
   * }
   */
  const trackProductListPitsClick = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          pits_index: index,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          // 保留额外字段以兼容其他数据分析需求
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
        };

        // 如果提供了 tabName，添加到属性中
        if (tabName) {
          properties.tab_name = tabName;
        }

        // 添加活动标识
        properties.market_activity_id = marketActivityId;
        properties.market_activity_name = MARKET_ACTIVITY_NAME;

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc 2026夏季新款镜框预售商品曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   *   tab_name: 商品导航tab名称,
   * }
   */
  const trackProductListPitsExposure = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          pits_index: index,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          // 保留额外字段以兼容其他数据分析需求
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
        };

        // 如果提供了 tabName，添加到属性中
        if (tabName) {
          properties.tab_name = tabName;
        }

        // 添加活动标识
        properties.market_activity_id = marketActivityId;
        properties.market_activity_name = MARKET_ACTIVITY_NAME;

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer product exposure tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Click
   * @EventDesc 2026夏季新款镜框预售广告位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   tab_name: tab按钮名称,
   *   pits_index: 坑位排序
   * }
   */
  const trackAdClick = useCallback(
    (
      moduleName: string,
      tabName: string,
      index: number,
      jumpType?: string,
      jumpContent?: string,
    ) => {
      try {
        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          tab_name: tabName,
          pits_index: index,
          pits_jump_type: jumpType || '',
          pits_jump_content: jumpContent || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer ad click tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Exp
   * @EventDesc 2026夏季新款镜框预售广告位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   tab_name: tab按钮名称,
   *   pits_index: 坑位排序,
   * }
   */
  const trackAdExp = useCallback(
    (
      moduleName: string,
      tabName: string,
      index: number,
      jumpType?: string,
      jumpContent?: string,
    ) => {
      try {
        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          tab_name: tabName,
          pits_index: index,
          pits_jump_type: jumpType || '',
          pits_jump_content: jumpContent || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer ad exp tracking error:', error);
      }
    },
    [trackEvent, marketActivityId],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignNotifyMeBtn_Click
   * @EventDesc 2026夏季新款镜框预售Notify me按钮点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   btn_name: btn按钮名称,
   *   product_id: 商品ID,
   *   product_sku: 商品SKU,
   *   business_sku: 平台SKU,
   *   shop_sku: 销售SKU,
   *   product_name: 商品名称,
   *   product_class_name: 商品类目名称
   * }
   */
  const trackNotifyMeClick = useCallback(
    (item: any, moduleName: string, pitsIndex?: number, tabName?: string) => {
      try {
        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          btn_name: 'notify me',
          notifyme_source: NOTIFYME_SOURCE,
          user_status: userStatus,
          product_id: item.id ? String(item.id) : '',
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_name: item.short_name || item.name,
          product_class_name: item.category_name || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (typeof pitsIndex === 'number') {
          properties.pits_index = pitsIndex;
        }

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignNotifyMeBtn_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer notify me tracking error:', error);
      }
    },
    [trackEvent, marketActivityId, userStatus],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageNotifyMe_Submit
   * @EventDesc 2026夏季新款镜框预售Notify me成功
   */
  const trackNotifyMeSubmit = useCallback(
    (item: any, moduleName: string, pitsIndex?: number, tabName?: string) => {
      try {
        const properties: any = {
          page_name: PAGE_NAME,
          page_id: marketActivityId,
          module_name: moduleName,
          notifyme_source: NOTIFYME_SOURCE,
          submit_result: 'success',
          user_status: userStatus,
          product_id: `${item.id}`,
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_name: item.short_name || item.name,
          product_class_name: item.category_name || '',
          market_activity_id: marketActivityId,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (typeof pitsIndex === 'number') {
          properties.pits_index = pitsIndex;
        }

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignPageNotifyMe_Submit',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('French Summer notify me submit tracking error:', error);
      }
    },
    [trackEvent, marketActivityId, userStatus],
  );

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息（参考denim-glasses实现）
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackViewMoreClick,
    trackBannerClick,
    trackBannerShow,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackAdClick,
    trackAdExp,
    trackNotifyMeClick,
    trackNotifyMeSubmit,
  };
}
